import { hashKey } from "@tanstack/react-query";
import axios from "axios";
import {
  IMPACT_REPORT_DEFERRAL_MAX_AGE_MS,
  IMPACT_REPORT_DEFERRAL_STORAGE_PREFIX,
  IMPACT_REPORT_FALLBACK_IMAGE_SRC,
  IMPACT_REPORT_HERO_STAT_CAP,
  REPORT_FILTER_WIRE_ENTRIES,
} from "@/constants/impactReport";
import type {
  AssembledStatementPart,
  CauseSection,
  ClientImpactReportDeferred,
  DashboardReportErrorResponse,
  ImpactReport,
  ImpactReportDeferralStorageEntry,
  ImpactTypeStat,
  ReportData,
  ReportFilter,
  ReportSummary,
} from "@/interfaces/impactReport";
import type {
  ImpactReportCardNarrative,
  ImpactReportCardRow,
  ImpactReportCardSortMode,
  ImpactReportCausePage,
  ImpactReportCoverPage,
  ImpactReportPage,
  ImpactReportStatistic,
} from "@/interfaces/impactReportView";

/**
 * Flattens a failed report fetch into a console-legible context object: the API's own
 * `DashboardReportErrorCode` and HTTP status when the response carried one, the axios transport
 * code otherwise.
 *
 * THE RAW ERROR IS NEVER INCLUDED, AND THIS IS THE ONLY IMPLEMENTATION OF THAT RULE — every
 * consumer that logs a failed report fetch (the `/impact` page and the card grid both do) calls
 * THIS function, so the redaction cannot drift between call sites. An `AxiosError` transitively
 * holds `config.headers`, which carries the live `Authorization: Bearer <clerk jwt>` set by the
 * request interceptor (`lib/apiClient.ts`) — logging it would serialize a replayable session
 * credential into the console, where PostHog's console capture, a support screenshot or a copied
 * dump can carry it off-device (OWASP A09:2021). Only the request's method/url and the stack
 * string survive.
 *
 * For the same reason, never "improve" this by adding `error.config`, `error.request` or
 * `error.toJSON()` to the returned object: each one re-attaches the headers this function exists
 * to strip.
 */
export function describeImpactReportError(error: unknown): Record<string, unknown> {
  if (axios.isAxiosError<DashboardReportErrorResponse>(error)) {
    return {
      status: error.response?.status ?? null,
      code: error.response?.data?.error?.code ?? null,
      apiMessage: error.response?.data?.error?.message ?? null,
      transportCode: error.code ?? null,
      message: error.message,
      method: error.config?.method ?? null,
      url: error.config?.url ?? null,
      stack: error.stack ?? null,
    };
  }
  return {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? (error.stack ?? null) : null,
  };
}

/**
 * True when a failed report fetch means the reporting service is down or refused Platform —
 * the API's `UPSTREAM_UNAVAILABLE` code, or a bare 503 when the body carried no code.
 */
export function isImpactReportUnavailableError(error: unknown): boolean {
  const { code, status } = describeImpactReportError(error);
  return code === "UPSTREAM_UNAVAILABLE" || status === 503;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * True when the report request was deferred to a background job (the 202 body) rather than
 * answered with a report. A report envelope always carries `data`; a deferral never does.
 *
 * `jobId` and `recipient` are checked for real (non-empty string) content, not merely their
 * presence: `useImpactReportJob` polls `jobId` and `ImpactReportDeferredState` renders
 * `recipient` verbatim, so a body that passed a looser check (`status === "deferred"` alone)
 * could poll an empty jobId forever or print "we'll email it to" nobody. `isValidStoredEntry`
 * below reuses this same guard, so a malformed sessionStorage entry self-heals the same way.
 */
export function isClientImpactReportDeferred(value: unknown): value is ClientImpactReportDeferred {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { status?: unknown }).status === "deferred" &&
    isNonEmptyString((value as { jobId?: unknown }).jobId) &&
    isNonEmptyString((value as { recipient?: unknown }).recipient) &&
    !("data" in value)
  );
}

/**
 * Names which of `isClientImpactReportDeferred`'s checks a value failed — for a `console.error`
 * at a 202 call site (`api/impactReport.ts`), never the value itself: `recipient` is a client
 * contact's email address, and logging it would put it in a console a support screenshot or
 * PostHog capture could carry off-device (the same rule `describeImpactReportError` enforces for
 * the auth header, above). Field names only.
 */
export function describeImpactReportDeferralGuardFailure(value: unknown): string[] {
  if (typeof value !== "object" || value === null) return ["body is not an object"];
  const record = value as { status?: unknown; jobId?: unknown; recipient?: unknown };
  const reasons: string[] = [];
  if (record.status !== "deferred") reasons.push("status");
  if (!isNonEmptyString(record.jobId)) reasons.push("jobId");
  if (!isNonEmptyString(record.recipient)) reasons.push("recipient");
  if ("data" in record) reasons.push("data (unexpected)");
  return reasons;
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE DEFERRAL SESSIONSTORAGE CACHE — keeps a deferred (202) answer alive past react-query's own
   `gcTime` and a page reload, keyed by USER and filter.

   A DEFERRED answer is held `staleTime: Infinity` in `hooks/useImpactReport.ts`, but react-query
   still garbage-collects the query `gcTime` (default 5 minutes) after its last observer unmounts.
   The selection lives in `ImpactReportProvider`, which survives navigation, so returning to
   `/impact` after the tab has spent 5+ minutes elsewhere — or reloading the page — would otherwise
   re-issue the GET with no cache entry to answer from. EVERY re-issued request starts a brand-new
   upstream job and sends ANOTHER email — it is never a cheap re-read. These three functions persist
   the deferral in `sessionStorage` so the SAME deferral answers the query again with no network
   call, for as long as the browser tab's session lasts (and the entry's own 7-day window, below).

   EVERY ENTRY POINT TAKES `userId: string | null` AS ITS FIRST ARGUMENT — the signed-in user's
   Clerk id (`hooks/useImpactReport.ts` reads it via `useUser()`), or `null` while Clerk is still
   loading or signed out. `null` means "read and write nothing": a session with no known owner must
   never touch storage, rather than falling back to some shared/anonymous bucket. This is what stops
   a SECOND user signing in on the same browser tab, with the same campaign selection, from being
   served the FIRST user's stored deferral — their job id and their `recipient` email — before the
   page ever reaches the network. The react-query cache key (`impactReportQueryKey`, just below)
   deliberately stays filter-only, with NO user id in it — but that does NOT mean the in-memory
   cache is leak-proof on its own: a query like `["impactReportJob", jobId]` is held
   `staleTime: Infinity` (see `hooks/useImpactReport.ts`), so within its `gcTime` window a second
   user signing in on the same tab (session expiry, or a sign-out/sign-in in another tab with no
   reload here) could otherwise be served the first user's cached job or report. `App.tsx`'s
   `QueryCacheUserScopeGuard` is what actually closes that gap — it clears the whole react-query
   cache whenever the resolved Clerk user id changes — so this sessionStorage layer and that guard
   cover two different lifetimes of the SAME risk, not two unrelated concerns.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `useImpactReport`'s react-query key for one filter. Exported so both that hook and this
 * module's own storage key (below) read the SAME definition — the whole point of hashing it is
 * that a stored deferral is filed under exactly the key react-query would use to look it up, and
 * two independent copies of this function could drift.
 */
export function impactReportQueryKey(filter: ReportFilter) {
  return ["impactReport", filter] as const;
}

/**
 * The sessionStorage key for one user's, one filter's deferral. `hashKey` — react-query's OWN
 * query-key hasher (`@tanstack/react-query`, re-exported from `@tanstack/query-core`) — is reused
 * rather than a hand-rolled `JSON.stringify`: it already sorts object keys recursively, so two
 * filters that are the same set of fields in a different property order hash identically, exactly
 * as react-query itself would treat them as the same cache entry.
 *
 * `userId` is a REQUIRED, non-null string here — every exported function below checks for `null`
 * itself before ever reaching this — so the user segment can never silently collapse to `"null"`
 * or `"undefined"` and accidentally become a shared bucket.
 */
function impactReportDeferralStorageKey(userId: string, filter: ReportFilter): string {
  return `${IMPACT_REPORT_DEFERRAL_STORAGE_PREFIX}${userId}:${hashKey(impactReportQueryKey(filter))}`;
}

/**
 * True when a value read back out of `JSON.parse` is a well-formed, still-fresh storage entry.
 * Never trusts the shape of anything read from sessionStorage without this — a hand-edited or
 * stale-schema value from a previous deploy must fail closed (read as "nothing stored") rather
 * than being handed to a consumer as a live deferral.
 */
function isValidStoredEntry(value: unknown): value is ImpactReportDeferralStorageEntry {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { storedAt?: unknown }).storedAt === "number" &&
    isClientImpactReportDeferred((value as { deferral?: unknown }).deferral)
  );
}

/**
 * Reads a still-valid stored deferral for `userId` + `filter`, or `null` when there is none — no
 * signed-in `userId`, no entry, an unparsable/malformed entry, or one older than
 * `IMPACT_REPORT_DEFERRAL_MAX_AGE_MS`. A rejected entry is removed as a side effect, so the next
 * read (and the next `storeImpactReportDeferral`) starts clean.
 *
 * Every `sessionStorage` access is wrapped in try/catch: private-browsing mode and a full storage
 * quota both throw on access rather than failing silently, and this must never surface as a
 * broken report — it simply behaves as if nothing were stored, same as before this cache existed.
 */
export function readStoredImpactReportDeferral(
  userId: string | null,
  filter: ReportFilter,
): ClientImpactReportDeferred | null {
  if (userId === null) return null;
  const key = impactReportDeferralStorageKey(userId, filter);

  let raw: string | null;
  try {
    raw = sessionStorage.getItem(key);
  } catch (error) {
    console.warn("readStoredImpactReportDeferral: sessionStorage is unavailable", error);
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    console.warn(
      "readStoredImpactReportDeferral: stored entry was not valid JSON; discarding it",
      error,
    );
    clearStoredImpactReportDeferral(userId, filter);
    return null;
  }

  if (!isValidStoredEntry(parsed)) {
    clearStoredImpactReportDeferral(userId, filter);
    return null;
  }

  if (Date.now() - parsed.storedAt > IMPACT_REPORT_DEFERRAL_MAX_AGE_MS) {
    clearStoredImpactReportDeferral(userId, filter);
    return null;
  }

  return parsed.deferral;
}

/**
 * Stores `deferral` for `userId` + `filter`, stamped with the current time. A `null` `userId` is a
 * no-op — see the section banner above for why an unowned session must never write here. Called
 * the moment `useImpactReport`'s `queryFn` sees a fresh 202 — see that hook for why a re-request
 * must never reach the network again for the same user and filter within this tab's session.
 */
export function storeImpactReportDeferral(
  userId: string | null,
  filter: ReportFilter,
  deferral: ClientImpactReportDeferred,
): void {
  if (userId === null) return;
  const entry: ImpactReportDeferralStorageEntry = { deferral, storedAt: Date.now() };
  try {
    sessionStorage.setItem(impactReportDeferralStorageKey(userId, filter), JSON.stringify(entry));
  } catch (error) {
    console.warn("storeImpactReportDeferral: failed to persist the deferral", error);
  }
}

/**
 * Forgets `userId` + `filter`'s stored deferral. A `null` `userId` is a no-op, same as the other
 * two functions above. Called alongside `useDiscardImpactReport`'s cache eviction in two cases:
 * a deferral whose job FAILED (without this, re-selecting the same filter would read the failed
 * deferral straight back out of sessionStorage and never issue the new request that starts a
 * fresh job), and a deferral whose job Crunchy has since expired or lost (`REPORT_JOB_NOT_FOUND`)
 * — see `pages/ImpactReport.tsx`'s "Build it again" action.
 */
export function clearStoredImpactReportDeferral(userId: string | null, filter: ReportFilter): void {
  if (userId === null) return;
  try {
    sessionStorage.removeItem(impactReportDeferralStorageKey(userId, filter));
  } catch (error) {
    console.warn("clearStoredImpactReportDeferral: failed to clear the stored deferral", error);
  }
}

/**
 * Forgets EVERY stored deferral, for every user — each key under
 * `IMPACT_REPORT_DEFERRAL_STORAGE_PREFIX`. Called by `App.tsx`'s `QueryCacheUserScopeGuard`
 * alongside `queryClient.clear()` when a signed-in user signs out or is replaced, so the previous
 * user's job ids and `recipient` email do not stay in the tab's storage after they have left. Keys
 * are collected first and removed after: removing while indexing `sessionStorage.key(i)` would
 * shift the indices and skip entries.
 */
export function clearStoredImpactReportDeferrals(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(IMPACT_REPORT_DEFERRAL_STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) sessionStorage.removeItem(key);
  } catch (error) {
    console.warn("clearStoredImpactReportDeferrals: failed to clear the stored deferrals", error);
  }
}

/**
 * True when a failed job fetch means the job is gone or not the caller's — the API's
 * `REPORT_JOB_NOT_FOUND` code, or a bare 404 when the body carried no code.
 */
export function isImpactReportJobNotFoundError(error: unknown): boolean {
  const { code, status } = describeImpactReportError(error);
  return code === "REPORT_JOB_NOT_FOUND" || status === 404;
}

/**
 * Serializes a report filter into the query string `GET /reports/client-impact-report` expects.
 *
 * Which fields exist and how each is spelled is NOT decided here — it is declared once, and
 * exhaustively, in `constants/impactReport.ts#REPORT_FILTER_WIRE_ENCODING`. This function only
 * knows the three encodings.
 *
 * Arrays are REPEATED KEYS (`cause=a&cause=b`) via `URLSearchParams.append`, matching the
 * encoding the API's inbound DTO reads back; comma-joining would fail silently as an ignored
 * filter. An unselected/empty array is skipped entirely rather than sent as an empty param: the
 * API rejects `?orderIds=` (key present, empty value) with a 400, so emitting one would break an
 * ordinary unfiltered page load.
 *
 * COUNTERPART: `apps/gfgf-api/src/utils/impact-report-helper.ts#buildReportFilterQueryString`.
 */
export function buildReportFilterQueryString(filter: ReportFilter): string {
  const params = new URLSearchParams();

  for (const [field, encoding] of REPORT_FILTER_WIRE_ENTRIES) {
    const value = filter[field];
    if (value === undefined) continue;

    switch (encoding) {
      case "scalar":
      case "boolean":
        params.append(field, String(value));
        break;
      case "repeated":
        if (!Array.isArray(value)) {
          // Unreachable through the type system — only array-typed fields can be declared
          // "repeated". Reported rather than dropped in silence because a filter field vanishing
          // from the request is exactly the failure this mechanism exists to stop: the user would
          // still be shown a report, answering a question they did not ask.
          console.error(
            `buildReportFilterQueryString: filter field "${field}" is declared "repeated" but carried a non-array value (${typeof value}); it was NOT sent`,
          );
          break;
        }
        for (const item of value) params.append(field, String(item));
        break;
      default: {
        const unhandledEncoding: never = encoding;
        throw new Error(
          `buildReportFilterQueryString: unhandled wire encoding ${String(unhandledEncoding)}`,
        );
      }
    }
  }

  return params.toString();
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE PAGE MODEL — payload in, an ordered array of pages out.
   Pure by contract: no React, no DOM, no fetching, no clock, no randomness. The PDF export
   stage re-renders this same array at fixed geometry, and a filter change is nothing more than
   re-running this over a new payload.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * THE ONE SANCTIONED PLACE PLATFORM COMPOSES PROSE.
 *
 * `summary.mainCause.causes` is a `string[]` and the wire cannot pick a separator for an unknown
 * count, so the join is a typographic decision left to the client. It is bounded to exactly this:
 * separators. The items arrive already rendered in their "-ing" form, so they are never cased,
 * trimmed or otherwise rewritten here — CSS does the casing.
 *
 *   0 items → ""            1 item → that item
 *   2+      → all but the last joined with ", ", then " & ", then the last
 */
function joinAuthoredCauses(causes: readonly string[]): string {
  if (causes.length <= 1) return causes.join("");

  const allButLast = causes.slice(0, -1).join(", ");
  const last = causes[causes.length - 1] ?? "";
  return `${allButLast} & ${last}`;
}

/**
 * The cover's main-cause line: `prependStr`, a space, the joined list, and exactly ONE trailing
 * period. An empty list prints `prependStr` alone — no dangling punctuation, no stray space.
 *
 * Exported because this composition, and not the raw join, is the carve-out: seeing the whole
 * composed line in one place is what keeps it reviewable.
 */
export function composeMainCauseDescriptor(prependStr: string, causes: readonly string[]): string {
  const joinedCauses = joinAuthoredCauses(causes);
  if (joinedCauses === "") return prependStr;
  return `${prependStr} ${joinedCauses}.`;
}

/**
 * The large-type slot: `formattedQuantity`, with `unit` after a single space when the unit is
 * non-empty. Returns `null` when there is no quantity at all to set.
 *
 * NOTE this is NOT how the below-floor state is detected — see `resolveDeclarativeStatistic`,
 * which branches on `suppressed` before ever reaching here.
 */
function composeStatisticValue(statement: AssembledStatementPart): string | null {
  const value =
    statement.unit === ""
      ? statement.formattedQuantity
      : `${statement.formattedQuantity} ${statement.unit}`;
  return value === "" ? null : value;
}

/**
 * One `ImpactTypeStat` → the one statistic the HERO renders for it, or `null` for "print
 * nothing at all".
 *
 * The contract names three states, and a consumer MUST branch on `suppressed` rather than on the
 * truthiness of `formattedQuantity` / `unit`:
 *
 *   present, suppressed: false → number + unit large, sentence beneath.
 *   present, suppressed: true  → the server withheld the figure below its own display floor, so
 *                                the hero omits the impact type ENTIRELY — same as an absent
 *                                register, and dropped before the cap so it never costs a slot.
 *   key ABSENT                 → no authored declarative copy exists, so the hero omits the
 *                                impact type entirely.
 *
 * The hero prints only complete statements. A tile that says something happened but declines to
 * say how much reads as a broken report on a document the client forwards onward, which is worse
 * than a shorter grid. The suppressed sentence is not lost: the CARD view renders it, because
 * suppression is per-register and that view reads `narrative` where this one reads `declarative`.
 *
 * `totalQuantity` is deliberately NOT a fallback for the absent case. It ships unconditionally so
 * a consumer can chart, sort or sum — analytical uses — not so Platform can print a bare figure
 * the server declined to format, with no unit and no sentence, on a document the client forwards
 * to their own leadership. The card view renders these metrics instead, as image +
 * `impactTypeName`, so nothing is lost: it appears in the view whose job is completeness.
 */
function resolveDeclarativeStatistic(impactType: ImpactTypeStat): ImpactReportStatistic | null {
  const statement = impactType.statements.declarative;
  if (statement === undefined) return null;

  // Still branching on the FLAG, never on the truthiness of `formattedQuantity` — a suppressed
  // row and an absent register are different states that happen to share an empty string.
  if (statement.suppressed) return null;

  const value = composeStatisticValue(statement);

  // Neither a number nor a sentence: an empty grid cell reads worse than a shorter grid.
  if (value === null && statement.sentence === "") return null;

  return {
    statisticKey: impactType.referenceValueId ?? impactType.impactTypeName,
    value,
    descriptor: statement.sentence,
  };
}

/**
 * One cause page. The statistic list is FILTERED BEFORE IT IS CAPPED: an impact type with no
 * declarative register must not consume one of the six slots, which is exactly what capping
 * first would let it do — silently printing five tiles where six were available.
 *
 * The cap is a plain slice of what remains, in the order received. There is no spill page, no
 * "(continued)" page, and no re-sorting to make the cap fairer; array order decides what prints.
 * Fewer than six tiles is a correct outcome, not a gap.
 */
function buildCausePage(cause: CauseSection, kicker: string): ImpactReportCausePage {
  const statistics = cause.impactTypes
    .map(resolveDeclarativeStatistic)
    .filter((statistic): statistic is ImpactReportStatistic => statistic !== null)
    .slice(0, IMPACT_REPORT_HERO_STAT_CAP);

  return {
    kind: "cause",
    // The opaque stored value used as IDENTITY only — never parsed, split, cased, and never
    // rendered. It is unique per entry, which an index is not once a filter drops a cause.
    pageKey: `cause:${cause.causeValue}`,
    kicker,
    // The authored "-ing" label. NEVER `causeValue`, and never derived from it.
    title: cause.causeName,
    backdropUrl: cause.image?.url ?? IMPACT_REPORT_FALLBACK_IMAGE_SRC,
    statistics,
  };
}

/**
 * The summary/cover page — the account described as a whole, in three figures.
 *
 * The vendor and type counts are the payload's own and are NOT recomputed against whatever filter
 * produced the payload; see the contract note on `ReportData.causeData` for why the counts and the
 * page list legitimately differ.
 */
function buildCoverPage(summary: ReportSummary, kicker: string): ImpactReportCoverPage {
  return {
    kind: "cover",
    pageKey: "cover",
    kicker,
    title: summary.headline.tagline,
    backdropUrl: summary.image?.url ?? null,
    statistics: [
      {
        statisticKey: "mainCause",
        value: String(summary.mainCause.numCauses),
        descriptor: composeMainCauseDescriptor(
          summary.mainCause.prependStr,
          summary.mainCause.causes,
        ),
      },
      {
        statisticKey: "impactVendors",
        value: String(summary.impactVendors.numVendors),
        descriptor: summary.impactVendors.subhead,
      },
      {
        statisticKey: "impactTypes",
        value: String(summary.impactTypes.numTypes),
        descriptor: summary.impactTypes.subhead,
      },
    ],
  };
}

/**
 * The whole document as an ordered page list: the cover, one page per `data.causeData` entry, then
 * the closing page.
 *
 * `causeData` is emitted in the ORDER RECEIVED. The server already orders it to match
 * `summary.mainCause.causes`; it is never sorted, filtered or reconciled against a local list of
 * causes here. There is no client-side notion of which causes exist or what order they belong in.
 *
 * `suppressCoverPage` is the ONE editorial decision this builder cannot make for itself: it is a
 * fact about the reader's filter selection, and this module is given only the payload. It is a
 * required argument rather than an option defaulting to `false` so that every consumer — the
 * screen today, the PDF export stage next — has to state which document it is asking for, instead
 * of inheriting a default that quietly disagrees with the other. The decision lives here, in the
 * array that IS the contract with every consumer, and never as a filter a renderer applies to the
 * result: a PDF that disagrees with the screen about which pages exist is the exact failure this
 * boundary exists to prevent.
 *
 * The CLOSING page is emitted unconditionally. It carries the GfG mark and the document's brand
 * sign-off, which the client asked for on every report however it is filtered, so it is not
 * conditioned on the cover, on the filter, or on anything else.
 *
 * Still pure: no React, no DOM, no fetching, no clock. Suppressing the cover changes which pages
 * are returned and nothing about what any remaining page says — see
 * `resolveImpactReportPhotoCredits` below for the one consequence that reaches outside this array.
 */
export function buildImpactReportPageModel(
  report: ImpactReport,
  suppressCoverPage: boolean,
): ImpactReportPage[] {
  const { orgName, summary, causeData, thankYou } = report.data;

  // A report with nothing to report is NO DOCUMENT — not a cover and a closing wrapped around
  // nothing. This decision lives here rather than in the renderer because this array is the whole
  // contract with every consumer: the PDF export stage re-renders exactly what this returns, so a
  // renderer-side check would have to be re-implemented there to avoid emitting a two-page PDF
  // for a client with no causes. An empty array says the same thing to every consumer at once.
  if (causeData.length === 0) return [];

  // `orgName` is legitimately `""` when the org mirror row is missing (the contract pairs that
  // with a meta warning). Every line that names the org therefore has an org-less form, decided
  // HERE and never in a component: an absent name must read as absent, not as broken copy.
  //
  // The kicker is dropped WHOLE in that state rather than trimmed down to a bare "IS", which
  // would print above every cause headline as an orphaned verb. `""` is the renderer's signal to
  // omit the element entirely — the same explicitly-absent convention `ImpactReportStatistic`
  // already uses for a withheld value.
  const kicker = orgName === "" ? "" : `${orgName} IS`;

  // KEEP THIS BRANCH: `thankYou.message` has shipped, but the schema key stays optional so
  // replayed pre-release cache entries (recorded before the field existed) still validate — an
  // absent `thankYou` is a real, ongoing state, not a transitional one. The composed fallback
  // below is a SECOND exception to "Platform formats, never translates" — the only one besides
  // the causes join. The org-less branch exists because interpolating an empty `orgName` would
  // set the document's final headline as ", thank you for Doing Good with us" — a leading comma
  // at display size.
  const closingTitle =
    thankYou?.message ??
    (orgName === ""
      ? "Thank you for Doing Good with us"
      : `${orgName}, thank you for Doing Good with us`);

  return [
    ...(suppressCoverPage ? [] : [buildCoverPage(summary, kicker)]),
    ...causeData.map((cause) => buildCausePage(cause, kicker)),
    {
      kind: "closing",
      pageKey: "closing",
      title: closingTitle,
      backdropUrl: thankYou?.image?.url ?? null,
    },
  ];
}

/**
 * The page number the cover occupies in the document Crunchy paginated. The ONE page number
 * Platform ever names, and it is named because the contract states it outright rather than because
 * anything here worked it out.
 */
const IMPACT_REPORT_COVER_PAGE_NUMBER = 1;

/**
 * The photo credits that describe the document `buildImpactReportPageModel` actually emitted,
 * given the SAME `suppressCoverPage` decision it was given.
 *
 * `thankYou.photoCredits` carries CRUNCHY'S page numbers, produced against Crunchy's own
 * pagination. Crunchy owns pagination and Platform has never computed a page number, so when the
 * cover survives these pass through untouched — the array is returned by reference, and an absent
 * `thankYou` stays `undefined` rather than becoming an empty list, because the colophon reads that
 * distinction.
 *
 * When the cover is suppressed, the document the reader holds no longer matches those numbers:
 * every remaining page has moved up one, and the cover's own photograph is not in the document at
 * all. So the cover's credit is DROPPED — crediting a photograph nobody can see is a false
 * statement rather than a generous one — and each surviving credit is renumbered down by the one
 * page removed ahead of it.
 *
 * THIS IS NOT A REIMPLEMENTATION OF CRUNCHY'S PAGINATION AND MUST NOT BECOME ONE. It uses exactly
 * two facts, both of them Platform's own: the cover is page 1, and Platform removed exactly that
 * one page from the front. Nothing here derives a page number from the payload — not the
 * thank-you page's, not a cause page's, and never from `causeData.length`, whose relationship to
 * the summary's counts the contract names as an invariant that must never be encoded.
 */
export function resolveImpactReportPhotoCredits(
  photoCredits: NonNullable<ReportData["thankYou"]>["photoCredits"] | undefined,
  suppressCoverPage: boolean,
): NonNullable<ReportData["thankYou"]>["photoCredits"] | undefined {
  if (photoCredits === undefined || !suppressCoverPage) return photoCredits;

  return photoCredits
    .filter((credit) => credit.page !== IMPACT_REPORT_COVER_PAGE_NUMBER)
    .map((credit) => ({ ...credit, page: credit.page - 1 }));
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE CARD VIEW — the same payload, flattened across causes, read in the NARRATIVE register.
   Pure by the same contract as the page model above: no React, no DOM, no fetching, no clock.

   Nothing below ever reads `statements.declarative`, and nothing above ever reads
   `statements.narrative`. That separation is the point: the two registers ship different units
   for one metric (5,439 hours declarative, 326,355 minutes narrative), so a fallback from one to
   the other renders a figure that is silently wrong under the other's wording.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Locale-aware, case-insensitive ordering of impact type names, built once.
 *
 * `sensitivity: "base"` compares as authored while ignoring case and accents — the STORED string
 * is never lowercased, slugified or otherwise transformed, here or anywhere: the comparison is
 * the only thing allowed to fold it, and only for the duration of the comparison.
 */
const IMPACT_TYPE_NAME_COLLATOR = new Intl.Collator(undefined, { sensitivity: "base" });

/**
 * A card's stable identity. Three parts, each earning its place: the cause it was flattened out
 * of, the row's own identifier (`referenceValueId`, which the contract allows to be `null`, so
 * the authored name stands in), and its position WITHIN that cause as the collision backstop for
 * the null-id case.
 *
 * The position is payload position, captured once at flatten time — not a render index. Sorting
 * moves rows without touching their keys, so a sort toggle re-orders the DOM instead of
 * remounting every card and re-requesting every image.
 */
function buildImpactReportCardKey(
  cause: CauseSection,
  impactType: ImpactTypeStat,
  indexWithinCause: number,
): string {
  // `causeValue` used as IDENTITY only — opaque, never parsed, cased or rendered.
  return `${cause.causeValue}:${indexWithinCause}:${impactType.referenceValueId ?? impactType.impactTypeName}`;
}

/**
 * One `ImpactTypeStat` → what the card draws for it.
 *
 * The contract names THREE states, and they are detected by branching on the KEY'S PRESENCE and
 * then on `suppressed` — never on the truthiness of `formattedQuantity` or `unit`. A withheld
 * register and an absent one both leave `formattedQuantity` empty, so a string test cannot tell
 * them apart, and they render differently: withheld still prints its sentence.
 *
 *   present, suppressed: false → figure in the large slot, sentence beneath.
 *   present, suppressed: true  → NO large slot (the server withheld the figure below this
 *                                metric's own display floor), sentence still printed — it carries
 *                                the fallback phrase Crunchy already chose. Platform never picks
 *                                between copy variants; `suppressed` is read for LAYOUT only.
 *   key ABSENT                 → neither. The tile still exists — this view is the complete
 *                                record — showing its image and its label alone.
 *
 * `totalQuantity` is deliberately NOT a fallback for the absent case. It is a raw, unformatted
 * number that the server declined to render, with no unit and no sentence around it; printing it
 * would be Platform inventing a figure on a document the client forwards to their own leadership.
 */
export function readImpactReportCardNarrative(
  impactType: ImpactTypeStat,
): ImpactReportCardNarrative {
  const statement = impactType.statements.narrative;
  if (statement === undefined) return { kind: "unauthored" };

  if (statement.suppressed) {
    return {
      kind: "withheld",
      sentence: statement.sentence,
      displayQuantity: statement.displayQuantity,
    };
  }

  // `unit` is read here and deliberately not rendered, whatever it holds. The card has no unit
  // slot: its `cardLabel` already names the unit ("Minutes of Employment"), so appending would set
  // "2,328 minutes" directly above "Minutes of Employment". The hero is where a unit belongs and
  // where it is composed onto the figure — see `composeStatisticValue` — and this view differing
  // from it is the intent, not an oversight to reconcile later.
  return {
    kind: "rendered",
    formattedQuantity: statement.formattedQuantity,
    sentence: statement.sentence,
    displayQuantity: statement.displayQuantity,
  };
}

/**
 * Every impact type in the payload, flattened out of its cause in payload order — the card view
 * IS `causeData.flatMap(c => c.impactTypes)`; there is deliberately no flat array on the wire.
 *
 * Nothing is filtered: not by value, not by unit, not for a missing image, not for a missing
 * register. The hero decides what prints; this view decides nothing, which is what makes it the
 * complete record.
 *
 * Nothing is de-duplicated either, and specifically not on `impactTypeName`: the contract assigns
 * each impact type to exactly ONE cause, so duplicates cannot occur — and if that assumption ever
 * loosened, a name-based de-dupe would silently drop real rows instead of surfacing the change.
 *
 * The result's length is never compared to `summary.impactTypes.numTypes` (nor `causeData`'s to
 * `numCauses`). The contract names those comparisons as invariants that MUST NOT be encoded: they
 * legitimately differ on correct data, because the counts are facts about the giving while
 * `causeData` is a fact about what could be rendered.
 */
export function flattenImpactReportCardRows(
  causeData: readonly CauseSection[],
): ImpactReportCardRow[] {
  return causeData.flatMap((cause) =>
    cause.impactTypes.map((impactType, indexWithinCause) => ({
      cardKey: buildImpactReportCardKey(cause, impactType, indexWithinCause),
      impactType,
      narrative: readImpactReportCardNarrative(impactType),
    })),
  );
}

/**
 * Alphabetical by `impactTypeName`, with `cardKey` as the tiebreak. The tiebreak is what makes
 * the order TOTAL: keys are unique per row, so rows the collator calls equal (two spellings that
 * differ only by case or accent) still land in one fixed order instead of reshuffling between
 * renders.
 */
function compareCardRowsByImpactTypeName(
  rowA: ImpactReportCardRow,
  rowB: ImpactReportCardRow,
): number {
  const byName = IMPACT_TYPE_NAME_COLLATOR.compare(
    rowA.impactType.impactTypeName,
    rowB.impactType.impactTypeName,
  );
  if (byName !== 0) return byName;

  if (rowA.cardKey < rowB.cardKey) return -1;
  if (rowA.cardKey > rowB.cardKey) return 1;
  return 0;
}

/**
 * The raw number this row sorts on, or `null` when the narrative register is absent entirely.
 *
 * A WITHHELD register still has one: suppression empties `formattedQuantity` and `unit`, never
 * `displayQuantity`, so a withheld row sorts on its true value rather than on a stand-in.
 */
function narrativeDisplayQuantity(row: ImpactReportCardRow): number | null {
  return row.narrative.kind === "unauthored" ? null : row.narrative.displayQuantity;
}

/**
 * Descending by `displayQuantity` — the raw number the contract ships for exactly this purpose,
 * so nothing has to parse commas or a currency symbol back out of `formattedQuantity`.
 *
 * Rows with NO narrative register sort LAST, alphabetically among themselves. A missing quantity
 * is never coerced to `0`: that would interleave "we have no authored copy for this" with metrics
 * whose real value is genuinely zero, two different facts presented as one.
 *
 * Equal quantities fall back to the alphabetical order, so the grid does not reshuffle between
 * renders. The comparisons are explicit rather than a subtraction so that a non-finite value
 * arriving on the wire falls through to that tiebreak instead of returning `NaN` and leaving the
 * sort's behaviour undefined.
 */
function compareCardRowsByNarrativeQuantity(
  rowA: ImpactReportCardRow,
  rowB: ImpactReportCardRow,
): number {
  const quantityA = narrativeDisplayQuantity(rowA);
  const quantityB = narrativeDisplayQuantity(rowB);

  if (quantityA === null || quantityB === null) {
    if (quantityA === null && quantityB === null)
      return compareCardRowsByImpactTypeName(rowA, rowB);
    return quantityA === null ? 1 : -1;
  }

  if (quantityA > quantityB) return -1;
  if (quantityA < quantityB) return 1;
  return compareCardRowsByImpactTypeName(rowA, rowB);
}

/**
 * The rows in the requested order. Sorting is entirely client-side — both registers ship on every
 * metric precisely so that changing the order costs no refetch.
 *
 * Copies before sorting: `Array.prototype.sort` sorts IN PLACE, and the input here is the
 * flattened payload that the rest of the view still holds.
 */
export function sortImpactReportCardRows(
  rows: readonly ImpactReportCardRow[],
  sortMode: ImpactReportCardSortMode,
): ImpactReportCardRow[] {
  switch (sortMode) {
    case "alphabetical":
      return [...rows].sort(compareCardRowsByImpactTypeName);
    case "quantity":
      return [...rows].sort(compareCardRowsByNarrativeQuantity);
    default: {
      const unhandledSortMode: never = sortMode;
      throw new Error(`sortImpactReportCardRows: unhandled sort mode ${String(unhandledSortMode)}`);
    }
  }
}
